/**
 * Guest-first access — runs the guest→account claim once an ACCOUNT session is
 * ready inside the app shell.
 *
 * Reads the single-use handoff, asks the server to claim the guest's hosted
 * conversations into the account, then invalidates the hosted history reads so
 * the sidebar shows what the backend stored. Nothing is written locally.
 *
 *   * ok            → handoff cleared; history refreshed; land on the claimed
 *                     counterpart of the conversation the guest was reading.
 *   * terminal fail → handoff cleared; a safe sentence is shown once.
 *   * retryable     → handoff kept; a retry action is offered.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";

import { claimGuestHistory } from "./guest.functions";
import type { GuestClaimFailureReason } from "./guestClaim";
import { clearGuestHandoff, readGuestHandoff } from "./guestHandoff";
import type { User } from "@/lib/looseend/types";

export function guestClaimFailureMessage(reason: GuestClaimFailureReason): string {
  switch (reason) {
    case "already_claimed":
      return "That guest conversation already belongs to another account.";
    case "guest_token_invalid":
      return "Your guest session had expired, so its conversation couldn't be brought along.";
    case "account_not_ready":
      return "Your account isn't ready to receive conversations yet.";
    case "caller_is_guest":
    case "same_identity":
      return "Nothing to bring along — you're already using this conversation.";
    default:
      return "We couldn't bring your guest conversation along. Please try again.";
  }
}

export function useGuestClaim(user: User | null, enabled: boolean): void {
  const claim = useServerFn(claimGuestHistory);
  const client = useQueryClient();
  const navigate = useNavigate();
  const attemptedFor = useRef<string | null>(null);

  const run = useCallback(async () => {
    if (!user || user.isGuest) return;
    const handoff = readGuestHandoff();
    if (!handoff) return;
    if (handoff.guestUserId === user.id) {
      // In-place identity upgrade: same user, same workspace, nothing to claim.
      clearGuestHandoff();
      return;
    }

    let outcome: Awaited<ReturnType<typeof claim>>;
    try {
      outcome = await claim({
        data: { guestUserId: handoff.guestUserId, ticket: handoff.ticket },
      });
    } catch {
      outcome = { ok: false, reason: "unavailable", retryable: true };
    }

    if (outcome.ok) {
      clearGuestHandoff();
      await client.invalidateQueries({ queryKey: ["hostedConversations"] });
      const fresh = outcome.value.filter((row) => !row.replayed);
      if (fresh.length > 0) {
        toast.success(
          fresh.length === 1
            ? "Your conversation is now saved to your account."
            : `${fresh.length} conversations are now saved to your account.`,
        );
      }
      const target = outcome.value.find(
        (row) => row.guestConversationId === handoff.conversationId,
      );
      if (target) {
        await navigate({
          to: "/app/chat/$conversationId",
          params: { conversationId: target.claimedConversationId },
          replace: true,
        });
      }
      return;
    }

    if (!outcome.retryable) {
      clearGuestHandoff();
      toast.error(guestClaimFailureMessage(outcome.reason));
      return;
    }
    toast.error(guestClaimFailureMessage(outcome.reason), {
      action: { label: "Try again", onClick: () => void run() },
    });
  }, [user, claim, client, navigate]);

  useEffect(() => {
    if (!enabled || !user || user.isGuest) return;
    if (attemptedFor.current === user.id) return;
    attemptedFor.current = user.id;
    void run();
  }, [enabled, user, run]);
}
